/**
 * /app/$tenantSlug/payroll/tax-declaration/$declId — HR per-employee verify
 * view. Each item: declared amount, proof viewer, verify / partial / reject;
 * sticky Finalise footer (step-up TOTP), enabled once all items are decided.
 *
 * RBAC: company_admin / hr_manager / accountant (backend boundary). Finalise
 * is step-up TOTP gated (totp_required → redirect to /2fa-verify; mirrors the
 * payroll lock-dialog).
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import {
  useDeclarationForReview,
  useEffectiveDeductions,
  useFinaliseDeclaration,
  useVerifyItem,
} from "@/hooks/use-tax-declaration";
import * as taxApi from "@/lib/api/tax-declaration";
import type { DeclarationItem } from "@/types/tax-declaration";
import { PageHeader } from "@/components/shared/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatPaise, rupeesToPaise } from "@/lib/expense-format";
import { toApiError } from "@/lib/api/client";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/tax-declaration/$declId",
)({
  component: DeclarationReviewRoute,
});

function DeclarationReviewRoute() {
  const { tenantSlug, declId } = useParams({ strict: false }) as {
    tenantSlug: string;
    declId: string;
  };
  const id = Number(declId);
  const { user } = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const { data, isLoading } = useDeclarationForReview(id, isAccountingAdmin(user?.role));
  const { data: effective } = useEffectiveDeductions(id, isAccountingAdmin(user?.role));
  const finalise = useFinaliseDeclaration(id);

  if (!isAccountingAdmin(user?.role)) {
    return (
      <>
        <PageHeader title="Tax declaration" description="Verification." />
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            You do not have access to verify tax declarations.
          </CardContent>
        </Card>
      </>
    );
  }

  const allDecided =
    data?.items.every(
      (i) =>
        i.item_status === "verified" ||
        i.item_status === "partially_verified" ||
        i.item_status === "rejected",
    ) ?? false;
  const canFinalise = data?.status === "under_review" && allDecided;

  function doFinalise() {
    finalise.mutate(undefined, {
      onSuccess: () => toast.success("Declaration finalised — verified amounts now drive TDS."),
      onError: (err) => {
        const e = toApiError(err);
        if (e.error === "totp_enrollment_required") {
          toast.error("Enrol in two-factor authentication to finalise.");
          void navigate({ to: "/2fa-setup", search: { required: 1, next: pathname } });
          return;
        }
        if (e.error === "totp_required") {
          toast.error("Two-factor verification required to finalise.");
          void navigate({ to: "/2fa-verify", search: { next: pathname } });
          return;
        }
        toast.error(e.message);
      },
    });
  }

  return (
    <>
      <PageHeader
        title={`Declaration #${declId}`}
        description="Verify each declared item against its proof, then finalise."
      />
      <div className="mb-4">
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/app/$tenantSlug/payroll/tax-declaration/queue" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" /> Back to queue
          </Link>
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {data && (
        <div className="space-y-4 pb-24">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{data.status}</Badge>
            <span className="text-sm text-muted-foreground">
              Employee #{data.employee_id} · FY {data.financial_year}
            </span>
          </div>

          {data.items.map((item) => (
            <VerifyRow key={item.id} declId={id} item={item} readOnly={data.status === "finalised"} />
          ))}

          {effective && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Effective deductions ({effective.source}, {effective.regime} regime)
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-1 text-sm sm:grid-cols-2">
                <span>Standard deduction: {formatPaise(effective.standard_deduction)}</span>
                <span>§80C: {formatPaise(effective.sec_80c)}</span>
                <span>§80CCD(1B): {formatPaise(effective.sec_80ccd_1b)}</span>
                <span>§80D: {formatPaise(effective.sec_80d)}</span>
                <span>HRA exempt: {formatPaise(effective.hra_exempt)}</span>
                <span>Home-loan interest: {formatPaise(effective.home_loan_interest)}</span>
                <span className="font-medium">
                  Total Chapter-VI-A: {formatPaise(effective.total_chapter_via)}
                </span>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {data && data.status === "under_review" && (
        <div className="fixed inset-x-0 bottom-0 border-t bg-background/95 p-4 backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {allDecided
                ? "All items decided — ready to finalise."
                : "Decide every item before finalising."}
            </span>
            <Button onClick={doFinalise} disabled={!canFinalise || finalise.isPending}>
              {finalise.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Finalise (2FA)
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function VerifyRow({
  declId,
  item,
  readOnly,
}: {
  declId: number;
  item: DeclarationItem;
  readOnly: boolean;
}) {
  const verify = useVerifyItem(declId);
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const locked = readOnly || item.item_status === "verified";

  function decide(
    status: "verified" | "partially_verified" | "rejected",
  ) {
    if (status === "rejected") {
      if (!reason.trim()) {
        toast.error("A rejection reason is required.");
        return;
      }
      verify.mutate(
        { itemId: item.id, body: { item_status: "rejected", rejection_reason: reason.trim() } },
        {
          onSuccess: () => toast.success("Item rejected"),
          onError: (e) => toast.error(toApiError(e).message),
        },
      );
      return;
    }
    const paise =
      status === "verified" && amount.trim() === ""
        ? item.declared_paise
        : rupeesToPaise(amount);
    if (paise == null) {
      toast.error("Invalid verified amount.");
      return;
    }
    verify.mutate(
      { itemId: item.id, body: { item_status: status, verified_paise: paise } },
      {
        onSuccess: () => toast.success("Item verified"),
        onError: (e) => {
          const err = toApiError(e);
          toast.error(
            err.error === "cap_exceeded"
              ? "Verified amount exceeds the statutory cap for this head."
              : err.message,
          );
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>{item.head}</span>
          <Badge
            variant={
              item.item_status === "verified" || item.item_status === "partially_verified"
                ? "default"
                : item.item_status === "rejected"
                  ? "destructive"
                  : "secondary"
            }
          >
            {item.item_status}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          Declared: <strong>{formatPaise(item.declared_paise)}</strong>
          {item.verified_paise != null && (
            <>
              {" · "}Verified: <strong>{formatPaise(item.verified_paise)}</strong>
            </>
          )}
        </p>
        {item.hra_landlord_pan_masked && (
          <p className="text-xs text-muted-foreground">
            Landlord PAN: {item.hra_landlord_pan_masked}
            {item.hra_annual_rent_paise != null && (
              <> · Annual rent {formatPaise(item.hra_annual_rent_paise)}</>
            )}
          </p>
        )}

        {/* Proofs */}
        <div className="flex flex-wrap gap-2">
          {item.proofs.map((p) => (
            <Button
              key={p.id}
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const blob = await taxApi.downloadProofForReview(declId, item.id, p.id);
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank", "noopener");
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } catch (e) {
                  toast.error(toApiError(e).message);
                }
              }}
            >
              View {p.file_name}
            </Button>
          ))}
          {item.proofs.length === 0 && (
            <span className="text-sm text-muted-foreground">No proofs uploaded.</span>
          )}
        </div>

        {!locked && (
          <div className="space-y-2 border-t pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                inputMode="decimal"
                placeholder={`Verified amount (₹) — defaults to declared`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="max-w-xs"
              />
              <Button size="sm" onClick={() => decide("verified")} disabled={verify.isPending}>
                Verify
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => decide("partially_verified")}
                disabled={verify.isPending}
              >
                Partial
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Textarea
                placeholder="Rejection reason (required to reject)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="max-w-md"
              />
              <Button
                size="sm"
                variant="destructive"
                onClick={() => decide("rejected")}
                disabled={verify.isPending}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
        {item.rejection_reason && (
          <p className="text-sm text-destructive">Rejected: {item.rejection_reason}</p>
        )}
      </CardContent>
    </Card>
  );
}
